/**
 * Sticky PDP Form
 * Shows/hides sticky product form based on main product section visibility
 * Handles variant selection synchronization with main product form
 */

(function () {
  'use strict';

  class StickyPdpForm {
    constructor(section) {
      this.section = section;
      this.productHandle = this.section.dataset.productHandle || null;

      if (!this.productHandle) {
        console.warn('StickyPdpForm: Product handle is not defined');
        return;
      }

      this.product = null;
      this.currentVariant = null;
      this.mainProductSection = document.querySelector('.main-product-section');
      this.footer = document.querySelector('.section-footer');

      if (!this.mainProductSection) {
        console.warn('StickyPdpForm: Main product section not found');
        return;
      }

      this.elements = {
        productTitle: this.section.querySelector('[data-product-title]'),
        variantImage: this.section.querySelector('[data-variant-image]'),
        priceComponent: this.section.querySelector('.product-price'),
        compareAtPrice: this.section.querySelector('[data-compare-price]'),
        productPrice: this.section.querySelector('[data-product-price]'),
        quantityInput: this.section.querySelector('[data-product-quantity]'),
        addToCartButton: this.section.querySelector('[data-add-to-cart]'),
        addToCartButtonText: this.section.querySelector('[data-add-to-cart-text]'),
        buttonPrice: this.section.querySelector('[data-button-price]'),
        variantInputs: this.section.querySelectorAll('[data-variant-input]'),
        variantIdSelect: this.section.querySelector('[data-product-select]'),
        form: this.section.querySelector('form'),
        toggleButton: this.section.querySelector('[data-toggle-button]'),
        formBody: this.section.querySelector('[data-form-body]'),
      };

      // Main form quantity input for syncing
      this.mainQuantityInput = this.mainProductSection?.querySelector('[data-product-quantity]');

      this.isExpanded = false;
      this.closeTimeout = null;
      this.focusableElements = [];
      this.firstFocusableElement = null;
      this.lastFocusableElement = null;
      this.isAjaxFormInitialized = false;
      this.isProcessingUpsells = false;

      this.init();
    }

    init() {
      this.fetchProductData();
      this.initializeScrollObserver();
      this.initializeFooterObserver();
      this.listenToMainProductVariantChanges();
      this.initThemeVariants();
      this.initializeToggle();
      this.initAjaxProductForm();
      this.initQuantitySync();
    }

    initAjaxProductForm() {
      // Initialize AJAX cart functionality for the sticky form
      // Prevent race condition: check flag immediately
      if (this.isAjaxFormInitialized) {
        console.log('⚠️ StickyPdpForm: AJAX form initialization already started');
        return;
      }

      // Set flag immediately to prevent race conditions
      this.isAjaxFormInitialized = true;

      // Wait for theme.js to be fully loaded if AjaxProduct isn't available yet
      if (!window.theme || !window.theme.AjaxProduct) {
        // theme.js not loaded yet, wait and retry
        let intervalCleared = false;
        const checkTheme = setInterval(() => {
          if (intervalCleared) return; // Extra safety check
          if (window.theme && window.theme.AjaxProduct) {
            intervalCleared = true;
            clearInterval(checkTheme);
            this.setupAjaxForm();
          }
        }, 50);

        // Timeout after 5 seconds to prevent infinite loop
        setTimeout(() => {
          intervalCleared = true;
          clearInterval(checkTheme);
        }, 5000);
        return;
      }

      this.setupAjaxForm();
    }

    setupAjaxForm() {
      if (!this.elements.form || !window.theme?.AjaxProduct) return;

      // Additional safeguard: check if form already has AjaxProduct data attribute
      if (this.elements.form.dataset.ajaxProductActive) {
        console.log('⚠️ StickyPdpForm: Form already has AjaxProduct initialized, skipping');
        return;
      }

      // Mark form as having AjaxProduct to prevent duplicate initialization
      this.elements.form.dataset.ajaxProductActive = 'true';

      const cartType = window.theme.settings?.cartType;

      // Check if there's a toggle mode upsell component on the page
      // Note: Upsells are in the main product form, but sticky form should include them
      const upsellComponent = document.querySelector('.component-product-upsells[data-upsell-mode="toggle"]');
      const hasToggleUpsells = !!upsellComponent;

      if (hasToggleUpsells) {
        // Handle form submission manually to include upsells
        // Use bound function reference to prevent duplicate handlers
        this.boundFormSubmitHandler = this.handleFormSubmitWithUpsells.bind(this);
        this.elements.form.addEventListener('submit', this.boundFormSubmitHandler, true);
        console.log('✅ StickyPdpForm: Upsell handler attached (form has upsells)');

        // Still initialize AjaxProduct for when no upsells are checked
        new window.theme.AjaxProduct(this.elements.form);
      } else {
        // Initialize AjaxProduct for both drawer and page cart types
        // This prevents native form submission which can fail in local dev
        new window.theme.AjaxProduct(this.elements.form);
        console.log('✅ StickyPdpForm: AjaxProduct initialized (cartType:', cartType, ')');
      }

      // Close the sticky form when product is added to cart (only for drawer)
      if (cartType === 'drawer') {
        this.elements.form.addEventListener('ajaxProduct:added', () => {
          // Small delay to allow user to see the success state before closing
          setTimeout(() => {
            this.closeForm();
          }, 200);
        });
      }
    }

    handleFormSubmitWithUpsells(evt) {
      // Prevent duplicate processing
      if (this.isProcessingUpsells) {
        console.log('⚠️ StickyPdpForm: Already processing, ignoring duplicate submit');
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }

      const checkedUpsells = this.getCheckedUpsells();

      if (checkedUpsells.length === 0) {
        // No upsells selected - let AjaxProduct handle it normally
        // Don't prevent default, just let the form submit
        return;
      }

      // Prevent all form submission handlers
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();

      // Add main product and upsells together
      this.addProductsWithUpsellsToCart(checkedUpsells);
    }

    getCheckedUpsells() {
      // Check for upsells globally (they're in the main product form)
      // The sticky form submits with these upsells included if checked
      const upsellContainer = document.querySelector('.component-product-upsells[data-upsell-mode="toggle"]');
      if (!upsellContainer) return [];

      const checkboxes = upsellContainer.querySelectorAll('.js-upsell-toggle:checked');
      return Array.from(checkboxes).map(checkbox => ({
        id: checkbox.dataset.variantId,
        quantity: 1
      }));
    }

    async addProductsWithUpsellsToCart(upsells) {
      const addToCartBtn = this.elements.addToCartButton;

      if (!addToCartBtn) return;

      // Double-check processing flag
      if (this.isProcessingUpsells) {
        console.log('⚠️ StickyPdpForm: Already adding to cart, ignoring duplicate request');
        return;
      }

      // Set processing flag immediately
      this.isProcessingUpsells = true;

      // Show loading state
      addToCartBtn.classList.add('is-loading');
      addToCartBtn.disabled = true;

      try {
        // Get main product form data
        const formData = new FormData(this.elements.form);
        const mainProductId = formData.get('id');
        const mainProductQuantity = formData.get('quantity') || 1;

        // Build items array with main product + upsells - ensure IDs are integers
        const items = [
          {
            id: parseInt(mainProductId),
            quantity: parseInt(mainProductQuantity)
          },
          ...upsells.map(upsell => ({
            id: parseInt(upsell.id),
            quantity: parseInt(upsell.quantity)
          }))
        ];

        // Add all items at once using the cart/add.js endpoint
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (data.status === 422 || !response.ok) {
          this.handleUpsellError(data);
        } else {
          this.handleUpsellSuccess(data);
        }
      } catch (error) {
        console.error('StickyPdpForm: Error adding products to cart:', error);
        this.handleUpsellError({ description: 'An error occurred. Please try again.' });
      } finally {
        // Remove loading state and reset processing flag
        addToCartBtn.classList.remove('is-loading');
        addToCartBtn.disabled = false;
        this.isProcessingUpsells = false;
      }
    }

    handleUpsellSuccess(data) {
      // Remove any existing errors
      const errors = this.elements.form.querySelector('.errors');
      if (errors) {
        errors.remove();
      }

      // Dispatch success event for cart drawer to open
      this.elements.form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: data.items ? data.items[0] : data,
          addToCartBtn: this.elements.addToCartButton
        },
        bubbles: true
      }));

      // Reload if on cart page
      if (document.body.classList.contains('template-cart')) {
        window.scrollTo(0, 0);
        location.reload();
      }
    }

    handleUpsellError(error) {
      const errorMessage = error.description || error.message || 'An error occurred';

      // Remove existing errors
      const existingErrors = this.elements.form.querySelector('.errors');
      if (existingErrors) {
        existingErrors.remove();
      }

      // Create error div
      const errorDiv = document.createElement('div');
      errorDiv.classList.add('errors', 'text-center');
      errorDiv.textContent = errorMessage;
      this.elements.form.append(errorDiv);

      // Dispatch error event
      this.elements.form.dispatchEvent(new CustomEvent('ajaxProduct:error', {
        detail: { errorMessage },
        bubbles: true
      }));
    }

    initQuantitySync() {
      // Sync quantity between sticky form and main product form
      if (!this.elements.quantityInput) return;

      // Listen for changes on sticky form quantity and sync to main form
      this.elements.quantityInput.addEventListener('change', (e) => {
        const newQuantity = e.target.value;
        if (this.mainQuantityInput && this.mainQuantityInput.value !== newQuantity) {
          this.mainQuantityInput.value = newQuantity;
          this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Listen for changes on main form quantity and sync to sticky form
      if (this.mainQuantityInput) {
        this.mainQuantityInput.addEventListener('change', (e) => {
          const newQuantity = e.target.value;
          if (this.elements.quantityInput && this.elements.quantityInput.value !== newQuantity) {
            this.elements.quantityInput.value = newQuantity;
          }
        });
      }
    }

    initializeToggle() {
      if (!this.elements.toggleButton) return;

      this.elements.toggleButton.addEventListener('click', () => {
        this.toggleForm();
      });

      // Close form when clicking outside
      document.addEventListener('click', (e) => {
        if (this.isExpanded &&
          !this.section.contains(e.target)) {
          this.closeForm();
        }
      });

      // Handle keyboard navigation
      this.handleKeyboardNavigation = this.handleKeyboardNavigation.bind(this);
    }

    handleKeyboardNavigation(e) {
      if (!this.isExpanded) return;

      // Close on Escape
      if (e.key === 'Escape') {
        this.closeForm();
        this.elements.toggleButton.focus();
        return;
      }

      // Trap focus with Tab
      if (e.key === 'Tab') {
        if (this.focusableElements.length === 0) return;

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === this.firstFocusableElement) {
            e.preventDefault();
            this.lastFocusableElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === this.lastFocusableElement) {
            e.preventDefault();
            this.firstFocusableElement.focus();
          }
        }
      }
    }

    updateFocusableElements() {
      // Get all focusable elements within the sticky form
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');

      this.focusableElements = Array.from(
        this.section.querySelectorAll(focusableSelectors)
      ).filter(el => {
        // Make sure element is visible
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
      });

      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    }

    toggleForm() {
      if (this.isExpanded) {
        this.closeForm();
      } else {
        this.openForm();
      }
    }

    openForm() {
      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      this.isExpanded = true;
      this.section.classList.add('is-expanded');
      this.elements.toggleButton.setAttribute('aria-expanded', 'true');
      this.elements.toggleButton.classList.add('is-outline');
      this.elements.toggleButton.setAttribute('aria-label', 'Close options');

      if (this.elements.backdrop) {
        this.elements.backdrop.classList.add('is-visible');
        this.elements.backdrop.setAttribute('aria-hidden', 'false');
      }

      // Setup focus trap - defer to avoid layout thrashing
      requestAnimationFrame(() => {
        this.updateFocusableElements();
        document.addEventListener('keydown', this.handleKeyboardNavigation);
      });

      // Focus first interactive element in the form body
      requestAnimationFrame(() => {
        const firstInput = this.elements.formBody?.querySelector('input, select, button');
        if (firstInput) {
          firstInput.focus();
        }
      });
    }

    closeForm() {
      if (!this.isExpanded) return;

      this.isExpanded = false;

      // Update button state immediately
      this.elements.toggleButton.setAttribute('aria-expanded', 'false');
      this.elements.toggleButton.setAttribute('aria-label', 'View options');
      this.elements.toggleButton.classList.remove('is-outline');

      if (this.elements.backdrop) {
        this.elements.backdrop.classList.remove('is-visible');
        this.elements.backdrop.setAttribute('aria-hidden', 'true');
      }

      // Remove focus trap immediately
      document.removeEventListener('keydown', this.handleKeyboardNavigation);

      // Wait for element fade-out (0.15s) before collapsing the form
      this.closeTimeout = setTimeout(() => {
        this.section.classList.remove('is-expanded');
        this.closeTimeout = null;
      }, 150);
    }

    async fetchProductData() {
      try {
        const response = await fetch(`/products/${this.productHandle}.js`);
        this.product = await response.json();

        // Sync with main product's current variant if available
        const mainVariantSelect = this.mainProductSection?.querySelector('[data-product-select]');
        if (mainVariantSelect && mainVariantSelect.value) {
          const mainVariantId = parseInt(mainVariantSelect.value, 10);
          const mainVariant = this.product.variants.find(v => v.id === mainVariantId);
          if (mainVariant) {
            this.currentVariant = mainVariant;
            this.updateVariantInfo(mainVariant);
            console.log('✅ StickyPdpForm: Synced with main product variant', mainVariant.id);
            return;
          }
        }

        // Fallback to first available variant
        this.currentVariant = this.product.variants.find(v => v.available) || this.product.variants[0];
      } catch (error) {
        console.error('StickyPdpForm: Error fetching product data', error);
      }
    }

    initializeScrollObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.section.classList.remove('is-visible');
            document.body.classList.remove('sticky-pdp-form-is-visible');
          } else {
            this.section.classList.add('is-visible');
            document.body.classList.add('sticky-pdp-form-is-visible');
          }
        });
      }, {
        root: null,
        threshold: 0.2
      });

      observer.observe(this.mainProductSection);
    }

    initializeFooterObserver() {
      if (!this.footer) {
        console.warn('StickyPdpForm: Footer not found');
        return;
      }

      const footerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Footer is visible, hide the sticky form
            this.section.classList.add('is-hidden-footer');
            document.body.classList.remove('sticky-pdp-form-is-visible');
          } else {
            // Footer is not visible, show the sticky form (if main section is not visible)
            this.section.classList.remove('is-hidden-footer');
            // Only add body class if the main section is not visible
            if (this.section.classList.contains('is-visible')) {
              document.body.classList.add('sticky-pdp-form-is-visible');
            }
          }
        });
      }, {
        root: null,
        threshold: 0
      });

      footerObserver.observe(this.footer);
    }

    initThemeVariants() {
      // Initialize theme.Variants for this form if we have variant inputs
      if (this.elements.variantInputs.length === 0 || !this.elements.form) return;

      const variantJson = this.section.querySelector('[data-variant-json]');
      if (!variantJson) {
        // Try to get variants from the main product section
        const mainVariantJson = this.mainProductSection?.querySelector('[data-variant-json]');
        if (mainVariantJson) {
          // Clone it to sticky form
          const clonedJson = mainVariantJson.cloneNode(true);
          this.section.appendChild(clonedJson);
        }
      }

      // Let theme.Variants handle the variant logic
      const variantsJsonEl = this.section.querySelector('[data-variant-json]');
      if (variantsJsonEl && window.theme && window.theme.Variants) {
        const variantsObject = JSON.parse(variantsJsonEl.innerHTML);

        const options = {
          container: this.section,
          enableHistoryState: false,
          singleOptionSelector: '[data-variant-input]',
          originalSelectorId: '[data-product-select]',
          variants: variantsObject
        };

        // Initialize theme.Variants which will handle all variant changes
        new window.theme.Variants(options);

        // Listen for variant changes on the sticky form itself (fired by theme.Variants)
        this.section.addEventListener('variantChange', (event) => {
          if (event.detail && event.detail.variant) {
            this.currentVariant = event.detail.variant;
            this.updateVariantInfo(event.detail.variant);
            // Sync with main product form
            this.syncWithMainProduct(event.detail.variant);
          }
        });
      }
    }

    listenToMainProductVariantChanges() {
      // Listen for variant changes from the main product form
      this.mainProductSection.addEventListener('variantChange', (event) => {
        if (event.detail && event.detail.variant) {
          this.updateFromMainProduct(event.detail.variant);
        }
      });

      // Also listen for the global variant:change event (but ignore our own events)
      document.addEventListener('variant:change', (event) => {
        // Skip if this event originated from the sticky form itself
        if (event.target === this.section || this.section.contains(event.target)) {
          return;
        }
        if (event.detail && event.detail.variant) {
          this.updateFromMainProduct(event.detail.variant);
        }
      });

      // Listen for sticky bar changes from main product
      this.mainProductSection.addEventListener('variantStickyBarChange', (event) => {
        if (event.detail && event.detail.variant) {
          this.updateFromMainProduct(event.detail.variant);
        }
      });
    }

    updateFromMainProduct(variant) {
      this.currentVariant = variant;
      this.updateVariantInputs(variant);
      this.updateVariantInfo(variant);
    }

    updateVariantInputs(variant) {
      // Update the radio buttons/inputs to match the main product form
      this.elements.variantInputs.forEach((input) => {
        const optionIndex = input.dataset.index;
        const optionValue = variant[optionIndex];

        if (input.type === 'radio') {
          if (input.value === optionValue) {
            input.checked = true;
          }
        } else if (input.tagName === 'SELECT') {
          input.value = optionValue;
        }
      });
    }

    syncWithMainProduct(variant) {
      // Find and update the main product form variant inputs
      const mainForm = this.mainProductSection.querySelector('form[action*="/cart/add"]');

      if (mainForm) {
        const mainVariantInputs = mainForm.querySelectorAll('[data-variant-input]');
        mainVariantInputs.forEach(input => {
          const optionIndex = input.dataset.index;
          const optionValue = variant[optionIndex];

          if (input.type === 'radio' && input.value === optionValue) {
            input.checked = true;
            // Trigger change to update main product
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }

    updateVariantInfo(variant) {
      // Update the variant ID select element (used by the form for submission)
      if (this.elements.variantIdSelect) {
        this.elements.variantIdSelect.value = variant.id;
        console.log('✅ StickyPdpForm: Updated variant ID select to', variant.id);
      }

      // Update image
      if (this.elements.variantImage) {
        let imageUrl = null;

        if (variant.featured_image && variant.featured_image.src) {
          imageUrl = variant.featured_image.src;
        } else if (variant.image) {
          imageUrl = variant.image;
        }

        if (imageUrl) {
          // Resize to 80x80 for thumbnail
          const resizedUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)/, '_80x80.$1');
          this.elements.variantImage.src = resizedUrl;
        }
      }

      // Update price using component-product-price structure
      if (this.elements.productPrice) {
        this.elements.productPrice.innerHTML = this.formatMoney(variant.price);
        if (variant.compare_at_price > variant.price) {
          this.elements.productPrice.classList.add('product-price__price--on-sale');
        } else {
          this.elements.productPrice.classList.remove('product-price__price--on-sale');
        }
      }

      // Update compare at price
      if (this.elements.compareAtPrice) {
        const compareWrap = this.elements.compareAtPrice.closest('[data-product-price-wrap]');
        if (variant.compare_at_price > variant.price) {
          this.elements.compareAtPrice.innerHTML = this.formatMoney(variant.compare_at_price);
          if (compareWrap) {
            compareWrap.classList.remove('hide');
          }
        } else {
          if (compareWrap) {
            compareWrap.classList.add('hide');
          }
        }
      }

      // Update button state and price
      if (this.elements.addToCartButton) {
        const defaultText = this.elements.addToCartButtonText?.dataset.defaultText || 'Add to cart';

        if (variant.available) {
          this.elements.addToCartButton.disabled = false;
          this.elements.addToCartButton.removeAttribute('disabled');
          if (this.elements.addToCartButtonText) {
            this.elements.addToCartButtonText.textContent = defaultText;
          }
          if (this.elements.buttonPrice) {
            this.elements.buttonPrice.innerHTML = this.formatMoney(variant.price);
            this.elements.buttonPrice.style.display = '';
          }
        } else {
          this.elements.addToCartButton.disabled = true;
          this.elements.addToCartButton.setAttribute('disabled', 'disabled');
          if (this.elements.addToCartButtonText) {
            this.elements.addToCartButtonText.textContent = window.theme?.strings?.soldOut || 'Sold out';
          }
          if (this.elements.buttonPrice) {
            this.elements.buttonPrice.style.display = 'none';
          }
        }
      }
    }

    formatMoney(cents) {
      // Use theme's money formatter if available, otherwise basic formatting
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        const moneyFormat = window.theme.settings?.moneyFormat || '${{amount}}';
        return window.theme.Currency.formatMoney(cents, moneyFormat);
      }

      // Fallback to basic money formatting
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }
  }

  // Initialize on DOM ready
  function initStickyPdpForm() {
    const section = document.querySelector('.sticky-pdp-form');

    if (!section) {
      return;
    }

    // Check if already initialized
    if (section.dataset.initialized === 'true') {
      return;
    }

    new StickyPdpForm(section);
    section.dataset.initialized = 'true';
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyPdpForm);
  } else {
    initStickyPdpForm();
  }

  // Reinitialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('.sticky-pdp-form');
    if (section) {
      // Reset all initialization flags for clean reload
      section.dataset.initialized = 'false';
      const form = section.querySelector('form');
      if (form) {
        delete form.dataset.ajaxProductActive;
        delete form.dataset.ajaxProductInitialized;
      }
      new StickyPdpForm(section);
      section.dataset.initialized = 'true';
    }
  });

})();
